import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BoardMedium_URL } from "../../config";

// Fetch chapters by subject
export const fetchChaptersBySubject = createAsyncThunk(
  "boardQuestion/fetchChaptersBySubject",
  async ({ subject_id, class_id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BoardMedium_URL}/chapters/by-subject/${class_id}/${subject_id}`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching chapters:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch question types
export const fetchQuestionTypes = createAsyncThunk(
  "boardQuestion/fetchQuestionTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `https://web1024.docmaster.in:5007/api/subjects/question-types`,
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching question types:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch question types by linking (board, medium, class, subject)
export const fetchQuestionTypesByLinking = createAsyncThunk(
  "boardQuestion/fetchQuestionTypesByLinking",
  async (
    { board_id, medium_id, class_id, subject_id },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get(
        `${BoardMedium_URL}/question-types-bylinking`,
        {
          params: { board_id, medium_id, class_id, subject_id },
        },
      );
      return response.data.data || response.data || [];
    } catch (error) {
      console.error("Error fetching question types by linking:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch difficulty levels
export const fetchDifficultyLevels = createAsyncThunk(
  "boardQuestion/fetchDifficultyLevels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `https://web1024.docmaster.in:5007/api/subjects/difficulty-levels`,
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching difficulty levels:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Add question with board and medium
export const addBoardQuestion = createAsyncThunk(
  "boardQuestion/addQuestion",
  async (questionData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BoardMedium_URL}/create-question-with-board-medium`,
        questionData,
      );
      return response.data;
    } catch (error) {
      console.error("Error adding question:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch all question types (including inactive)
export const fetchAllQuestionTypes = createAsyncThunk(
  "boardQuestion/fetchAllQuestionTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `https://web1024.docmaster.in:5007/api/classes/get-type`,
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching all question types:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteQuestionType = createAsyncThunk(
  "boardQuestion/deleteQuestionType",
  async (typeId, { rejectWithValue }) => {
    try {
      const res = await fetch(
        `${BoardMedium_URL}/delete-question-type/${typeId}`,
        { method: "DELETE" }
      );

      const data = await res.json();

      if (!res.ok) {
        return rejectWithValue(data.message);
      }

      return data; // return backend response
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
// Fetch mapped subjects for a question type
export const fetchMappedSubjects = createAsyncThunk(
  "boardQuestion/fetchMappedSubjects",
  async ({ board_id, medium_id, class_id, type_id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BoardMedium_URL}/mapped-subjects`, {
        params: { board_id, medium_id, class_id, type_id },
      });
      return response.data.data || response.data || [];
    } catch (error) {
      console.error("Error fetching mapped subjects:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Map question type to subjects
export const mapTypeToSubjects = createAsyncThunk(
  "boardQuestion/mapTypeToSubjects",
  async (mappingData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BoardMedium_URL}/map-type-subjects`,
        mappingData,
      );
      return response.data;
    } catch (error) {
      console.error("Error mapping type to subjects:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch questions with pagination
export const fetchBoardQuestions = createAsyncThunk(
  "boardQuestion/fetchBoardQuestions",
  async (
    { board_id, page = 1, medium_id, class_id, subject_id, chapter_id },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get(`${BoardMedium_URL}/questions`, {
        params: { board_id, page, medium_id, class_id, subject_id, chapter_id },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching board questions:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch single question by ID for editing
export const fetchQuestionById = createAsyncThunk(
  "boardQuestion/fetchQuestionById",
  async (questionId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BoardMedium_URL}/question/${questionId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching question:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const boardQuestionSlice = createSlice({
  name: "boardQuestion",
  initialState: {
    // Dropdown selections
    selectedMedium: null,
    selectedClass: null,
    selectedSubject: null,
    selectedChapter: null,
    disabledValue: false,

    // Data arrays
    chapters: [],
    questionTypes: [],
    linkedQuestionTypes: [],
    difficultyLevels: [],
    allQuestionTypes: [],
    mappedSubjects: [],
    questions: [],

    // Pagination
    currentPage: 1,
    totalPages: 1,
    totalQuestions: 0,
    questionsPerPage: 10,

    // Edit
    editData: null,
    editDataLoading: false,

    // Loading states
    chaptersLoading: false,
    questionTypesLoading: false,
    linkedQuestionTypesLoading: false,
    difficultyLevelsLoading: false,
    allQuestionTypesLoading: false,
    mappedSubjectsLoading: false,
    mappingTypeToSubjects: false,
    questionsLoading: false,
    addingQuestion: false,

    // Error
    error: null,
  },
  reducers: {
    setSelectedMedium: (state, action) => {
      state.selectedMedium = action.payload;
      state.selectedClass = null;
      state.selectedSubject = null;
      state.selectedChapter = null;
      state.chapters = [];
    },

    setSelectedClass: (state, action) => {
      state.selectedClass = action.payload;
      state.selectedSubject = null;
      state.selectedChapter = null;
      state.chapters = [];
    },
    setSelectedSubject: (state, action) => {
      state.selectedSubject = action.payload;
      state.selectedChapter = null;
      state.chapters = [];
    },
    setSelectedChapter: (state, action) => {
      state.selectedChapter = action.payload;
    },
    resetQuestionForm: (state) => {
      state.selectedMedium = null;
      state.selectedClass = null;
      state.selectedSubject = null;
      state.selectedChapter = null;
      state.chapters = [];
      state.error = null;
      state.disabledValue = false;
    },
    toggleStatus: (state, action) => {
      state.disabledValue = action.payload;
    },
    setEditData: (state, action) => {
      state.editData = action.payload;
    },
    setAllSelections: (state, action) => {
      const { medium, classId, subject, chapter } = action.payload;
      if (medium) state.selectedMedium = medium;
      if (classId) state.selectedClass = classId;
      if (subject) state.selectedSubject = subject;
      if (chapter) state.selectedChapter = chapter;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch chapters by subject
      .addCase(fetchChaptersBySubject.pending, (state) => {
        state.chaptersLoading = true;
        state.error = null;
      })
      .addCase(fetchChaptersBySubject.fulfilled, (state, action) => {
        state.chapters = action.payload || [];
        state.chaptersLoading = false;
      })
      .addCase(fetchChaptersBySubject.rejected, (state, action) => {
        state.chaptersLoading = false;
        state.error = action.payload || "Failed to fetch chapters";
      })
      // Fetch question types
      .addCase(fetchQuestionTypes.pending, (state) => {
        state.questionTypesLoading = true;
      })
      .addCase(fetchQuestionTypes.fulfilled, (state, action) => {
        state.questionTypes = action.payload || [];
        state.questionTypesLoading = false;
      })
      .addCase(fetchQuestionTypes.rejected, (state, action) => {
        state.questionTypesLoading = false;
        state.error = action.payload || "Failed to fetch question types";
      })
      // Fetch question types by linking
      .addCase(fetchQuestionTypesByLinking.pending, (state) => {
        state.linkedQuestionTypesLoading = true;
      })
      .addCase(fetchQuestionTypesByLinking.fulfilled, (state, action) => {
        state.linkedQuestionTypes = action.payload || [];
        state.linkedQuestionTypesLoading = false;
      })
      .addCase(fetchQuestionTypesByLinking.rejected, (state, action) => {
        state.linkedQuestionTypesLoading = false;
        state.error = action.payload || "Failed to fetch linked question types";
      })
      // Fetch difficulty levels
      .addCase(fetchDifficultyLevels.pending, (state) => {
        state.difficultyLevelsLoading = true;
      })
      .addCase(fetchDifficultyLevels.fulfilled, (state, action) => {
        state.difficultyLevels = action.payload || [];
        state.difficultyLevelsLoading = false;
      })
      .addCase(fetchDifficultyLevels.rejected, (state, action) => {
        state.difficultyLevelsLoading = false;
        state.error = action.payload || "Failed to fetch difficulty levels";
      })
      // Add question
      .addCase(addBoardQuestion.pending, (state) => {
        state.addingQuestion = true;
        state.error = null;
      })
      .addCase(addBoardQuestion.fulfilled, (state) => {
        state.addingQuestion = false;
      })
      .addCase(addBoardQuestion.rejected, (state, action) => {
        state.addingQuestion = false;
        state.error = action.payload || "Failed to add question";
      })
      // Fetch all question types
      .addCase(fetchAllQuestionTypes.pending, (state) => {
        state.allQuestionTypesLoading = true;
        state.error = null;
      })
      .addCase(fetchAllQuestionTypes.fulfilled, (state, action) => {
        state.allQuestionTypes = action.payload || [];
        state.allQuestionTypesLoading = false;
      })
      .addCase(fetchAllQuestionTypes.rejected, (state, action) => {
        state.allQuestionTypesLoading = false;
        state.error = action.payload || "Failed to fetch all question types";
      })
      // Fetch mapped subjects
      .addCase(fetchMappedSubjects.pending, (state) => {
        state.mappedSubjectsLoading = true;
        state.error = null;
      })
      .addCase(fetchMappedSubjects.fulfilled, (state, action) => {
        state.mappedSubjects = action.payload || [];
        state.mappedSubjectsLoading = false;
      })
      .addCase(fetchMappedSubjects.rejected, (state, action) => {
        state.mappedSubjectsLoading = false;
        state.error = action.payload || "Failed to fetch mapped subjects";
      })
      // Map type to subjects
      .addCase(mapTypeToSubjects.pending, (state) => {
        state.mappingTypeToSubjects = true;
        state.error = null;
      })
      .addCase(mapTypeToSubjects.fulfilled, (state) => {
        state.mappingTypeToSubjects = false;
      })
      .addCase(mapTypeToSubjects.rejected, (state, action) => {
        state.mappingTypeToSubjects = false;
        state.error = action.payload || "Failed to map type to subjects";
      })
      // Fetch board questions
      .addCase(fetchBoardQuestions.pending, (state) => {
        state.questionsLoading = true;
        state.error = null;
      })
      .addCase(fetchBoardQuestions.fulfilled, (state, action) => {
        state.questions = action.payload.data || action.payload.questions || [];
        state.currentPage =
          action.payload.currentPage || action.payload.page || 1;
        state.totalPages = action.payload.totalPages || 1;
        state.totalQuestions =
          action.payload.total || action.payload.totalRecords || 0;
        state.questionsPerPage =
          action.payload.perPage || action.payload.limit || 10;
        state.questionsLoading = false;
      })
      .addCase(fetchBoardQuestions.rejected, (state, action) => {
        state.questionsLoading = false;
        state.error = action.payload || "Failed to fetch questions";
      })
      // Fetch single question by ID
      .addCase(fetchQuestionById.pending, (state) => {
        state.editDataLoading = true;
        state.editData = null;
      })
      .addCase(fetchQuestionById.fulfilled, (state, action) => {
        state.editData = action.payload;
        state.editDataLoading = false;
      })
      .addCase(fetchQuestionById.rejected, (state, action) => {
        state.editDataLoading = false;
        state.error = action.payload || "Failed to fetch question";
      });
    // .addCase(deleteQuestionType.pending, (state) => {
    //   state.editDataLoading = true;
    //   state.editData = null;
    // })
    // .addCase(deleteQuestionType.fulfilled, (state, action) => {
    //   state.editData = action.payload;
    //   state.editDataLoading = false;
    // })q
    // .addCase(deleteQuestionType.rejected, (state, action) => {
    //   state.editDataLoading = false;
    //   state.error = action.payload || "Failed to fetch question";
    // });
  },
});

export const {
  setSelectedMedium,
  setSelectedClass,
  setSelectedSubject,
  setSelectedChapter,
  resetQuestionForm,
  toggleStatus,
  setEditData,
  setAllSelections,
} = boardQuestionSlice.actions;

export default boardQuestionSlice.reducer;
